// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the parts of an AYNI Khipu (`.kpu`, `schema/ayni-khipu-v1.0.json`,
 * `docs/AYNI_XR_KHIPU_PLAN.md`) the app reads, and the pure rules over a
 * lesson Khipu: its card in the Lessons tab (`lessonSummary`), the copy
 * served to a teacher (`publicLesson`, editorial notes removed), and a
 * teacher's own lesson made from an AYNI canvas (`adaptLesson`). Since
 * 2026-10-07 also the other kinds the Khipu player plays (stories,
 * session records): their steps' media, layers and timing, their log, and
 * the minimal exchange copy a plan that keeps no Khipus downloads
 * (`exchangeKhipu`).
 *
 * Written from scratch for AYNI (owner, 2026-10-05): nothing here comes
 * from TerraViz's code. Shared by the browser, the
 * lessons route (`functions/api/lessons/`) and `scripts/build-lessons.ts`,
 * so it holds no DOM and no Worker globals.
 */

/** A localized text: `{ en: '…', es: '…' }`. */
export type KhipuText = Record<string, string>

export interface KhipuDatasetRef {
  key: string
  /** `stream:<name>` (an AYNI stream) or `sos:<id>` (the NOAA library). */
  ref: string
  title?: KhipuText
  dsaId?: string
  legacyId?: string
  units?: string
  dataEnabled?: boolean
  [extra: string]: unknown
}

export interface KhipuPrompt {
  kind: 'question' | 'task' | 'rationale' | string
  audience?: 'students' | 'presenter' | 'everyone' | string
  text: KhipuText
}

/** The format version new Khipus are written with (1.2: a step's `satellites`, 2026-10-24). Readers take any 1.x. */
export const KHIPU_SCHEMA_VERSION = '1.2'

/**
 * Khipu 1.2 (owner, 2026-10-24; docs/AYNI_XR_SATELLITE_CATALOG_PLAN.md §4a):
 * the Satellites layer as a step sets it. Absent: left as the viewer had it.
 */
export interface KhipuSatellites {
  on: boolean
  /** Show sets ('highlights', 'all', 'starlink', 'with-datasets', the debris groups). */
  show?: string[]
  colourBy?: 'orbit' | 'country' | 'purpose'
  /** The satellite selected (its card beside it), by NORAD id; null: none. */
  selected?: number | null
  /** Its clock: live, or minutes from now (a Khipu plays on any day), the speed and whether it runs. */
  clock?: { live?: boolean; offsetMinutes?: number; speed?: number; playing?: boolean }
  altitude?: 'true' | 'compact'
  mapHeight?: 'ground' | 'compact' | 'true'
  /** Field of view circles. */
  fov?: boolean
  trails?: boolean
  /** The browser globe keeps the selected satellite in view while the step holds. */
  follow?: boolean
  /** AR: Sky above me, where the session can show it; ignored on a screen. */
  sky?: boolean
}

export interface KhipuView {
  lat: number
  lon: number
  zoom: number
  bearing?: number
  pitch?: number
  /** AYNI (2026-10-19): shown on the flat 2:1 map (docs/AYNI_XR_FLAT_MAP_PLAN.md); absent: the globe. */
  flat?: boolean
}

export interface KhipuTime {
  at?: string
  relative?: 'latest' | 'first'
  playing?: boolean
  rate?: number
}

/** A picture, clip or link a step shows beside the globe (https only). */
export interface KhipuMedia {
  kind: 'image' | 'video' | 'panorama' | 'link'
  url: string
  title?: KhipuText
  caption?: KhipuText
  credit?: string
}

/** AYNI (multi-globe plan): one of the other globes a step shows, by its dataset key, with its time rule (`globeSlots.ts` short form). */
export interface KhipuGlobe {
  dataset: string | null
  time?: string
  [extra: string]: unknown
}

/** A layer a step puts on (or, for a canvas, suggests), by its dataset key. */
export interface KhipuLayer {
  role: 'basemap' | 'overlay' | 'realtime'
  dataset: string
  tint?: 'white' | 'black' | 'source'
  /** What to look for once the layer is on (a lesson's question linking a real-time variable to the step's dataset). */
  prompt?: KhipuText
  /** AYNI (owner, 2026-10-12): a real-time layer's opacity, 0 … 1 (absent: opaque). */
  opacity?: number
  /** AYNI (owner, 2026-10-12): a real-time layer hidden with its eye (it plays in step, unseen). */
  hidden?: boolean
}

/** How a step moves on: when the person playing it says, or after some seconds. */
export interface KhipuAdvance {
  mode: 'manual' | 'timer'
  seconds?: number
}

/** A place on the globe. */
export interface KhipuPlace {
  lat: number
  lon: number
}

/** Camera moves into a step (Khipu 1.1, docs/AYNI_XR_STUDIO_MOTION_PLAN.md). */
export type KhipuMove = 'cut' | 'fly' | 'glide' | 'path'
/** What the camera does while a step holds. */
export type KhipuDuring = 'still' | 'orbit' | 'pushIn' | 'pullOut'
/** How the data changes into a step. */
export type KhipuDataMove = 'cut' | 'crossfade' | 'wipe' | 'timeRun' | 'basemap'
export type KhipuFeel = 'gentle' | 'snappy' | 'linear'

/** How a step is reached from the one before, and what the camera does while it holds (Khipu 1.1). */
export interface KhipuTransition {
  move?: KhipuMove
  seconds?: number
  feel?: KhipuFeel
  path?: KhipuPlace[]
  during?: KhipuDuring
  /** Degrees for orbit, zoom levels for pushIn / pullOut. */
  amount?: number
  data?: KhipuDataMove
  wipe?: 'meridian' | 'terminator'
}

/** What the picture does around the data during a step (Khipu 1.1); the data inside a spotlight is never altered. */
export interface KhipuEffects {
  spotlight?: KhipuPlace & { radius: number; dim?: number; desaturate?: boolean; outline?: boolean }
  depthBlur?: number
  titleCard?: { text: KhipuText; seconds?: number }
  callouts?: Array<KhipuPlace & { label: string; value?: boolean }>
  arrows?: Array<{ points: KhipuPlace[]; label?: string; color?: number }>
  counter?: KhipuPlace & { label?: string }
  compare?: { at?: string; from?: number; to?: number }
  vignette?: number
  grain?: number
}

export interface KhipuStep {
  id: string
  n: number
  title?: KhipuText
  dataset?: string | null
  view?: KhipuView | null
  time?: KhipuTime | null
  prompts?: KhipuPrompt[]
  layers?: KhipuLayer[]
  media?: KhipuMedia[]
  advance?: KhipuAdvance
  /** AYNI (multi-globe plan, 2026-10-10): the globe layout the step shows. */
  layout?: '1' | '2h' | '2v' | '4'
  /** AYNI (multi-globe plan): the other globes (2, 3, 4): each its dataset key (null: bare Earth) and its time rule in the short form. */
  globes?: KhipuGlobe[]
  /** Seconds the step stays on screen in a video, and how long its motion lasts in the player (Khipu 1.1). */
  hold?: number
  transition?: KhipuTransition
  effects?: KhipuEffects
  /** Khipu 1.2: the Satellites layer the step sets; null: off. */
  satellites?: KhipuSatellites | null
  /** What the person playing the step sets: view, time, region, pins, layers, display. */
  open?: string[]
  /**
   * AYNI (2026-10-15, AYNI 3D Presenter mode): where the legend and the date
   * label sit and how big they are. Screen players read only `show`.
   */
  labels?: KhipuStepLabels
  [extra: string]: unknown
}

/** A label placed on a sphere: centre latitude, start longitude, height in degrees. */
export interface KhipuSphereLabel {
  show?: boolean
  lat?: number
  lon?: number
  sizeDeg?: number
}

/** A step's label layout (AYNI 3D Presenter mode); every field is optional. */
export interface KhipuStepLabels {
  legend?: KhipuSphereLabel & { copies?: 1 | 4; widthPercent?: number }
  time?: KhipuSphereLabel & { format?: string }
}

export interface KhipuLessonBlock {
  grade?: number
  unit?: string
  unitTitle?: KhipuText
  drivingQuestion?: KhipuText
  /** The mistaken belief, as students say it. */
  misconception?: KhipuText
  /** What is actually true. */
  correction?: KhipuText
  exitTicket?: KhipuText
  standards?: Array<{ framework: string; code: string }>
  durationMinutes?: number
  flagship?: boolean
  teacherNotes?: KhipuText[]
  [extra: string]: unknown
}

/** One thing that happened, `t` ms after the log started (the plan's log events table). */
export interface KhipuEvent {
  t: number
  type: string
  step?: string
  actor?: string
  data?: Record<string, unknown>
  [extra: string]: unknown
}

export interface KhipuLog {
  startedAt: string
  endedAt?: string
  events: KhipuEvent[]
  [extra: string]: unknown
}

export interface Khipu {
  $schema?: string
  format: 'ayni-khipu'
  schemaVersion: string
  id: string
  slug?: string
  kind: 'lesson' | 'session' | 'briefing' | 'research' | 'story'
  status?: 'draft' | 'published' | 'archived'
  title: KhipuText
  languages?: string[]
  created: string
  modified?: string
  creator?: string
  license?: { spdx?: string; name?: string; url?: string }
  attribution?: string
  derivedFrom?: Array<{ id: string; slug?: string; relation: 'adapted-from' | 'recorded-from' | 'copy-of'; title?: KhipuText }>
  privacy: { mode: 'anonymous' | 'attributed'; [extra: string]: unknown }
  datasets: KhipuDatasetRef[]
  description?: KhipuText
  path: { steps: KhipuStep[] }
  log?: KhipuLog
  lesson?: KhipuLessonBlock
  editorial?: unknown
  [extra: string]: unknown
}

/** A lesson's card in the Lessons tab. */
export interface LessonSummary {
  slug: string
  id: string
  title: KhipuText
  unit: string
  unitTitle?: KhipuText
  grade: number | null
  standards: string[]
  flagship: boolean
  minutes: number | null
  steps: number
  drivingQuestion?: KhipuText
  languages: string[]
  /** The datasets' names, in step order, for the card. */
  datasets: KhipuText[]
}

/** The text in `lang`, else English, else the first one there is. */
/** AYNI (2026-10-11): a language whose own text is missing reads this one before English (Catalan reads Spanish). */
const CONTENT_FALLBACK: Readonly<Record<string, string>> = { ca: 'es' }

export function localized(text: KhipuText | undefined, lang = 'en'): string {
  if (!text) return ''
  const near = CONTENT_FALLBACK[lang]
  return text[lang] ?? (near ? text[near] : undefined) ?? text.en ?? Object.values(text)[0] ?? ''
}

export function lessonSummary(k: Khipu): LessonSummary {
  const lesson = k.lesson ?? {}
  const byKey = new Map(k.datasets.map((d) => [d.key, d]))
  const seen = new Set<string>()
  const datasets: KhipuText[] = []
  for (const step of k.path.steps) {
    const d = step.dataset ? byKey.get(step.dataset) : undefined
    if (d && !seen.has(d.key)) {
      seen.add(d.key)
      datasets.push(d.title ?? { en: d.ref })
    }
  }
  return {
    slug: k.slug ?? k.id,
    id: k.id,
    title: k.title,
    unit: lesson.unit ?? '',
    ...(lesson.unitTitle ? { unitTitle: lesson.unitTitle } : {}),
    grade: typeof lesson.grade === 'number' ? lesson.grade : null,
    standards: (lesson.standards ?? []).map((s) => s.code),
    flagship: lesson.flagship === true,
    minutes: typeof lesson.durationMinutes === 'number' ? lesson.durationMinutes : null,
    steps: k.path.steps.length,
    ...(lesson.drivingQuestion ? { drivingQuestion: lesson.drivingQuestion } : {}),
    languages: k.languages ?? ['en'],
    datasets,
  }
}

/** The lesson as a teacher gets it: the review notes AYNI keeps for itself removed. */
export function publicLesson(k: Khipu): Khipu {
  const { editorial: _editorial, ...rest } = k
  return rest
}

/** What a teacher set on one step of their copy. */
export interface StepSetting {
  view?: KhipuView
  time?: KhipuTime
}

export interface AdaptOptions {
  /** The new Khipu's id: 16 hex characters. */
  id: string
  /** ISO date-time. */
  now: string
  /** A credit line the teacher typed. Never filled in from an account. */
  creator?: string
}

const FIELD_OF: Record<keyof StepSetting, string> = { view: 'view', time: 'time' }

/**
 * A teacher's own lesson from an AYNI canvas: the steps they set carry
 * their camera and date, and those stop being open; everything else is
 * the canvas's. It records where it came from (`derivedFrom`,
 * `adapted-from`), keeps the canvas's licence (CC BY-NC-SA: an adaptation
 * stays under it) and its attribution, and drops AYNI's review notes.
 */
export function adaptLesson(k: Khipu, settings: Record<string, StepSetting>, opts: AdaptOptions): Khipu {
  const base = publicLesson(k)
  const steps = base.path.steps.map((step) => {
    const set = settings[step.id]
    if (!set) return step
    const next: KhipuStep = { ...step }
    const closed = new Set<string>()
    for (const field of ['view', 'time'] as const) {
      if (set[field]) {
        ;(next as Record<string, unknown>)[field] = set[field]
        closed.add(FIELD_OF[field])
      }
    }
    if (step.open) next.open = step.open.filter((f) => !closed.has(f))
    return next
  })
  const creator = opts.creator?.trim().slice(0, 200)
  return {
    ...base,
    id: opts.id,
    ...(base.slug ? { slug: `${base.slug}-adapted` } : {}),
    status: 'draft',
    created: opts.now,
    modified: opts.now,
    ...(creator ? { creator } : {}),
    derivedFrom: [{ id: k.id, ...(k.slug ? { slug: k.slug } : {}), relation: 'adapted-from', title: k.title }],
    path: { ...base.path, steps },
  }
}

/** A fresh 16-hex Khipu id. */
export function newKhipuId(random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array = (b) => crypto.getRandomValues(b)): string {
  return [...random(new Uint8Array(8))].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** A lesson is a canvas: it never moves the globe; every other kind plays its steps. */
export function isCanvas(k: Khipu): boolean {
  return k.kind === 'lesson'
}

/** The format a plan that keeps no Khipus downloads: the minimal exchange copy. */
export const EXCHANGE_FORMAT = 'ayni-khipu-exchange'

/** What the exchange copy keeps of a step. */
const EXCHANGE_STEP_FIELDS = ['id', 'n', 'title', 'dataset', 'view', 'time', 'prompts', 'media'] as const
/** What the exchange copy keeps of the lesson block (not AYNI's provenance: `absorbs`, `source`). */
const EXCHANGE_LESSON_FIELDS = ['grade', 'unit', 'unitTitle', 'drivingQuestion', 'misconception', 'correction', 'exitTicket', 'standards', 'durationMinutes', 'flagship', 'teacherNotes'] as const
/** What the exchange copy keeps of a dataset reference: enough to read the Khipu. */
const EXCHANGE_DATASET_FIELDS = ['key', 'ref', 'title', 'units'] as const

/**
 * The minimal exchange copy of a Khipu (owner, 2026-10-05: a plan that
 * keeps no Khipus downloads this, never the full format): identity,
 * title, languages, licence, credit, the lesson block (without AYNI's
 * review notes), the datasets by reference and the steps' titles,
 * datasets, prompts, camera, date and media. No log, privacy, integrity,
 * attachments or recording parts. Opening it again gives a Khipu that
 * plays the same steps (`khipuFile.normalizeKhipu`). Its schema is
 * `schema/ayni-khipu-exchange-v1.0.json` (not published yet).
 */
export function exchangeKhipu(k: Khipu): Record<string, unknown> {
  const pick = <T extends object>(o: T, fields: readonly string[]) =>
    Object.fromEntries(fields.filter((f) => (o as Record<string, unknown>)[f] !== undefined).map((f) => [f, (o as Record<string, unknown>)[f]]))
  return {
    format: EXCHANGE_FORMAT,
    schemaVersion: '1.0',
    id: k.id,
    ...(k.slug ? { slug: k.slug } : {}),
    kind: k.kind,
    title: k.title,
    ...(k.description ? { description: k.description } : {}),
    ...(k.languages ? { languages: k.languages } : {}),
    ...(k.creator ? { creator: k.creator } : {}),
    created: k.created,
    ...(k.license ? { license: k.license } : {}),
    ...(k.attribution ? { attribution: k.attribution } : {}),
    ...(k.lesson ? { lesson: pick(k.lesson, EXCHANGE_LESSON_FIELDS) } : {}),
    datasets: k.datasets.map((d) => pick(d, EXCHANGE_DATASET_FIELDS)),
    path: { steps: k.path.steps.map((st) => pick(st, EXCHANGE_STEP_FIELDS)) },
  }
}
